<!--
	DemoRequest — the workflow-demo enquiry (content/demo.ts): email, the
	vehicle/panel the installer is stuck on, scanner, plotter. Same contract as
	EmailCapture: a plain form POST to /api/demo-request with no JS; with JS it
	intercepts and swaps to a readout. The no-JS path lands on /demo-requested.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import Field from './Field.svelte';
	import Button from './Button.svelte';
	import Readout from './Readout.svelte';
	import { trackEvent } from '$lib/analytics';
	import { demo } from '$lib/content/demo';

	type Source = 'landing' | 'home' | 'pricing' | 'guide' | 'faq';
	type Status = 'idle' | 'submitting' | 'success' | 'error';

	let {
		source = 'landing',
		class: klass = ''
	}: {
		source?: Source;
		class?: string;
	} = $props();

	let email = $state('');
	let vehiclePanel = $state('');
	let scanner = $state('');
	let plotter = $state('');
	let status = $state<Status>('idle');
	let failedField = $state<'email' | 'vehiclePanel' | 'form' | null>(null);
	let message = $state('');

	// The compatibility checker links here with the visitor's picks in the URL.
	onMount(() => {
		const query = new URLSearchParams(location.search);
		scanner ||= query.get('scanner')?.slice(0, 120) ?? '';
		plotter ||= query.get('plotter')?.slice(0, 120) ?? '';
	});

	const formError = $derived(failedField === 'form' ? message : '');

	const validEmail = (e: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e);

	function fail(field: NonNullable<typeof failedField>, text: string) {
		status = 'error';
		failedField = field;
		message = text;
	}

	async function handleSubmit(e: SubmitEvent) {
		e.preventDefault();
		if (!validEmail(email)) return fail('email', demo.errors.email);
		if (vehiclePanel.trim().length < 2) return fail('vehiclePanel', demo.errors.vehiclePanel);
		status = 'submitting';
		failedField = null;
		try {
			const res = await fetch('/api/demo-request', {
				method: 'POST',
				headers: { 'content-type': 'application/json' },
				body: JSON.stringify({ email, vehiclePanel, scanner, plotter, source })
			});
			if (!res.ok) return fail('form', demo.errors.generic);
			const { eventId } = await res.json();
			status = 'success';
			const props = {
				lead_type: 'demo_request',
				placement: source,
				has_scanner: scanner.trim() !== '',
				has_plotter: plotter.trim() !== ''
			};
			trackEvent('Demo Request', { props, eventId }, 'generate_lead');
		} catch {
			fail('form', demo.errors.network);
		}
	}
</script>

{#if status === 'success'}
	<div class={klass} role="status">
		<Readout label="Status" value={demo.success.title} accent />
		<p class="mt-2 text-body text-bone-500">{demo.success.body}</p>
	</div>
{:else}
	<form class="dr {klass}" method="POST" action="/api/demo-request" onsubmit={handleSubmit}>
		<input type="hidden" name="source" value={source} />
		<!-- Honeypot: hidden from people and assistive tech, irresistible to bots. -->
		<div class="dr__hp" aria-hidden="true">
			<label>Website <input type="text" name="website" tabindex="-1" autocomplete="off" /></label>
		</div>
		<Field
			id="demo-{source}-email"
			label={demo.fields.email.label}
			name="email"
			type="email"
			placeholder={demo.fields.email.placeholder}
			autocomplete="email"
			required
			bind:value={email}
			error={failedField === 'email' ? message : undefined}
		/>
		<Field
			id="demo-{source}-vehicle"
			label={demo.fields.vehiclePanel.label}
			name="vehiclePanel"
			placeholder={demo.fields.vehiclePanel.placeholder}
			required
			bind:value={vehiclePanel}
			error={failedField === 'vehiclePanel' ? message : undefined}
		/>
		<div class="dr__pair">
			<Field
				id="demo-{source}-scanner"
				label={demo.fields.scanner.label}
				name="scanner"
				placeholder={demo.fields.scanner.placeholder}
				bind:value={scanner}
			/>
			<Field
				id="demo-{source}-plotter"
				label={demo.fields.plotter.label}
				name="plotter"
				placeholder={demo.fields.plotter.placeholder}
				bind:value={plotter}
			/>
		</div>
		{#if formError}
			<p class="dr__error" role="alert">{formError}</p>
		{/if}
		<Button type="submit" size="lg" loading={status === 'submitting'}>
			{status === 'submitting' ? 'Sending…' : demo.cta}
		</Button>
	</form>
{/if}

<style>
	.dr {
		display: flex;
		flex-direction: column;
		gap: 1rem;
		align-items: stretch;
	}
	.dr :global(button[type='submit']) {
		align-self: flex-start;
	}
	.dr__pair {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
		gap: 1rem;
	}
	.dr__hp {
		position: absolute;
		left: -9999px;
		width: 1px;
		height: 1px;
		overflow: hidden;
	}
	.dr__error {
		margin: 0;
		font-size: var(--text-body);
		color: var(--color-signal-red);
	}
</style>
